import type { NoticeBadge } from "./notice-badge";

export interface NoticeConversation {
  id: string;
  internalChannelId?: string | null;
  name: string;
  preview: string;
  lastMessageAt: string;
  online: boolean;
  /** Sendbird unseen. Clears when the mentor opens the chat. */
  unread: boolean;
  /** Teen sent the last message, or a pending activity/Hub post needs a reply. */
  needsResponse: boolean;
  checkIn: boolean;
  /** First 7 ET calendar days since match/rematch or channel create. */
  newTeen: boolean;
  badge: NoticeBadge | null;
  /** Unsigned mentee photo key; signed in the row before render. */
  avatarUrl?: string | null;
}

export type NoticeFilterId =
  | "all"
  | "needsResponse"
  | "unread"
  | "checkIn"
  | "newTeens";

export interface NoticeFilterCounts {
  needsResponse: number;
  unread: number;
  checkIn: number;
  newTeens: number;
}

export function getNoticeFilterCounts(
  conversations: NoticeConversation[],
): NoticeFilterCounts {
  return {
    needsResponse: conversations.filter(
      (conversation) => conversation.needsResponse,
    ).length,
    unread: conversations.filter((conversation) => conversation.unread).length,
    checkIn: conversations.filter((conversation) => conversation.checkIn)
      .length,
    newTeens: conversations.filter((conversation) => conversation.newTeen)
      .length,
  };
}

export function filterNoticeConversations(
  conversations: NoticeConversation[],
  filter: NoticeFilterId,
): NoticeConversation[] {
  switch (filter) {
    case "all":
      return conversations;
    case "needsResponse":
      return conversations.filter((conversation) => conversation.needsResponse);
    case "unread":
      return conversations.filter((conversation) => conversation.unread);
    case "checkIn":
      return conversations.filter((conversation) => conversation.checkIn);
    case "newTeens":
      return conversations.filter((conversation) => conversation.newTeen);
    default: {
      const exhaustiveCheck: never = filter;
      return exhaustiveCheck;
    }
  }
}

// Empty query returns the list untouched so search composes on top of the
// active quick-filter.
export function searchNoticeConversations(
  conversations: NoticeConversation[],
  query: string,
): NoticeConversation[] {
  const normalized = query.trim().toLowerCase();
  if (normalized === "") {
    return conversations;
  }
  return conversations.filter((conversation) =>
    conversation.name.toLowerCase().includes(normalized),
  );
}
